import { useEffect, useState } from 'react';
import { LibraryItem } from '../../../pages/library/components/library-list/types';
import { NWClient } from '../../../client/NWClient';
import { toast } from 'react-toastify';
import { ROUTES } from '../../../common/constants/routes';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AIContractModel } from '../../../models/ai.contract.model';
import { addDocumentContract } from '../../../slices/document-contracts/documentContractsSlice';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { selectUser } from '../../../slices/user/userSlice';
import { selectAllCompanies } from '../../../slices/companies/companiesSlice';
import {
  createBusinessProfileEntry,
  fetchBusinessProfileById,
  updateBusinessProfileEntry,
  selectBusinessProfileDetails,
  selectBusinessProfileStatus,
} from '../../../slices/business-profile/businessProfileSlice';
import { fetchEmployees } from '../../../slices/employees/employeesSlice';
import { fetchCustomers } from '../../../slices/customers/customersSlice';
import { fetchSuppliers } from '../../../slices/suppliers/suppliersSlice';
import { fetchBusinessProfileContactById } from '../../../slices/business-profile/businessProfileSlice';

export const useContractsGenerator = () => {
  const currentUser = useAppSelector(selectUser);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const path = location.pathname;
  const company = useAppSelector(selectAllCompanies)[0];
  const businessProfileDetails = useAppSelector(selectBusinessProfileDetails);
  const businessProfileStatus = useAppSelector(selectBusinessProfileStatus);
  const [generatorOpened, setGeneratorOpened] = useState<boolean>(false);
  const [analysisLoading, setAnalysisLoading] = useState<boolean>(false);
  const [planUpgradingMessage, setPlanUpgradingMessage] = useState<boolean>(false);
  const [secondPartyCategory, setSecondPartyCategory] = useState<string>('employment');
  const [secondParty, setSecondParty] = useState<string>('noSecondParty');
  const [checkingBusinessProfile, setCheckingBusinessProfile] = useState<boolean>(true);
  const [placeholders, setPlaceholders] = useState<{
    [key: string]: string;
  }>({});
  const [step, setStep] = useState<number>(1);
  const [aiContract, setAIContract] = useState<AIContractModel>(null);
  const handleGenerateTemplate = (
    libraryItem: LibraryItem,
    data: {
      user: number;
      document_library?: number;
      document_template?: number;
      company_id: number;
      secondary_id?: number;
      category: string;
    }
  ) => {
    setAnalysisLoading(true);
    NWClient.post('ai-contract', data)
      .then((res: AIContractModel) => {
        //toast.success(t('messages.contractGenerated'), { theme: 'colored' });
        setAIContract(res);
        setPlaceholders(res.placeholder_matches);
        setStep(2);
        setAnalysisLoading(false);
        localStorage.setItem('AIContractFail', 'false');
      })
      .catch((error) => {
        setAnalysisLoading(false);
        if (
          error.response?.data?.errors?.length > 0 &&
          error.response?.data?.errors[0] === t('messages.exceededPlanLimits')
        ) {
          setPlanUpgradingMessage(true);
        } else if (
          error.response?.data?.errors?.length > 0 &&
          error.response?.data?.errors[0] === t('messages.userNotAssociatedWithCompany')
        ) {
          navigate(`${ROUTES.COMPANY_CREATE}?backUrl=${path}`);
        } else {
          if (localStorage.getItem('AIContractFail') === 'true') {
            toast.error(error.message ? error.message : t('messages.errorOccurred'));
          } else {
            localStorage.setItem('AIContractFail', 'true');
            toast.error(t('messages.firstAIContractFail'));
          }
        }
      });
  };
  const createDocumentFromTemplate = (libraryItem: LibraryItem) => {
    setAnalysisLoading(true);
    const data = {
      user: currentUser.id,
      contract_generation: aiContract.id,
      name:
        (libraryItem.name ? libraryItem.name : t('documentPage.headerDefault')) +
        '_' +
        new Date().toISOString(),
      category: aiContract?.category,
    };
    const contractData = { ...aiContract };
    delete contractData.id;
    contractData.placeholder_matches = placeholders;
    NWClient.put('ai-contract', Number(aiContract.id), contractData, true)
      .then(() => {
        dispatch(addDocumentContract({ data }))
          .unwrap()
          .then(() => {
            toast.success(t('messages.contractCreated'));
            navigate(ROUTES.WORKSPACE);
          })
          .catch((error) => {
            if (
              error.errors?.length > 0 &&
              t('messages.exceededPlanLimits').includes(error.errors[0])
            ) {
              setPlanUpgradingMessage(true);
            } else {
              toast.error(error.message ? error.message : t('messages.errorOccurred'));
            }
          })
          .finally(() => setAnalysisLoading(false));
      })
      .catch((error) => {
        setAnalysisLoading(false);
        toast.error(error.message ? error.message : t('messages.errorOccurred'));
      });
  };

  useEffect(() => {
    dispatch(fetchEmployees({ company_id: company.id }));
    dispatch(fetchCustomers({ company_id: company.id }));
    dispatch(fetchSuppliers({ company_id: company.id }));
    dispatch(fetchBusinessProfileContactById({ id: company.id }));
  }, []);

  useEffect(() => {
    if (company) {
      dispatch(fetchBusinessProfileById({ id: company.id }))
        .unwrap()
        .finally(() => {
          setCheckingBusinessProfile(false);
        });
    } else {
      setCheckingBusinessProfile(false);
    }
  }, [company]);

  return {
    generatorOpened,
    setGeneratorOpened,
    analysisLoading,
    setAnalysisLoading,
    handleGenerateTemplate,
    planUpgradingMessage,
    setPlanUpgradingMessage,
    secondParty,
    secondPartyCategory,
    setSecondParty,
    setSecondPartyCategory,
    step,
    setStep,
    setPlaceholders,
    placeholders,
    aiContract,
    createDocumentFromTemplate,
    checkingBusinessProfile,
  };
};
